<script setup lang="ts">
defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  src?: string | null
  alt?: string
  fallbackIcon?: string
  width?: number | string
  height?: number | string
  loading?: 'eager' | 'lazy'
  decoding?: 'async' | 'auto' | 'sync'
}>(), {
  alt: '',
  decoding: 'async',
  fallbackIcon: 'i-tabler-photo-off',
  height: undefined,
  loading: 'lazy',
  src: undefined,
  width: undefined,
})

const attrs = useAttrs()

const loadState = ref<'pending' | 'loaded' | 'error'>('pending')

const resolvedSrc = computed(() => String(props.src || '').trim())

const showFallback = computed(() => !resolvedSrc.value || loadState.value === 'error')

const sizeStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width != null && props.width !== '')
    style.width = typeof props.width === 'number' ? `${props.width}px` : String(props.width)
  if (props.height != null && props.height !== '')
    style.height = typeof props.height === 'number' ? `${props.height}px` : String(props.height)
  return style
})

watch(resolvedSrc, (next) => {
  loadState.value = next ? 'pending' : 'error'
}, { immediate: true })

function onLoad() {
  loadState.value = 'loaded'
}

function onError() {
  loadState.value = 'error'
}
</script>

<template>
  <span
    class="b-image"
    :class="[
      attrs.class,
      {
        'b-image--error': showFallback,
        'b-image--loaded': loadState === 'loaded',
        'b-image--loading': loadState === 'pending' && !!resolvedSrc,
      },
    ]"
    :style="[attrs.style as string | Record<string, string> | undefined, sizeStyle]"
    :role="showFallback ? 'img' : undefined"
    :aria-label="showFallback && alt ? alt : undefined"
  >
    <span
      v-if="showFallback"
      class="b-image__fallback"
      aria-hidden="true"
    >
      <span :class="fallbackIcon" class="b-image__icon" />
    </span>
    <img
      v-else
      class="b-image__img"
      :src="resolvedSrc"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loading"
      :decoding="decoding"
      @load="onLoad"
      @error="onError"
    >
  </span>
</template>

<style lang="scss" scoped>
.b-image {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  line-height: 0;
  overflow: hidden;
  max-width: 100%;

  &--loading {
    background-image: linear-gradient(
      90deg,
      rgba(148, 163, 184, 0.12) 0%,
      rgba(148, 163, 184, 0.28) 50%,
      rgba(148, 163, 184, 0.12) 100%
    );
    background-size: 200% 100%;
    animation: b-image-shimmer 1.15s linear infinite;

    .b-image__img {
      opacity: 0.35;
    }
  }

  &--loaded {
    .b-image__img {
      animation: b-image-fade-in 220ms ease-out;
    }
  }

  &--error {
    min-width: 1.5em;
    min-height: 1.5em;
    color: rgba(148, 163, 184, 0.85);
  }

  &__img {
    display: block;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: inherit;
  }

  &__fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-width: 1.25em;
    min-height: 1.25em;
    padding: 0.2em;
    box-sizing: border-box;
  }

  &__icon {
    width: 60%;
    height: 60%;
    min-width: 14px;
    min-height: 14px;
    max-width: 28px;
    max-height: 28px;
  }
}

@keyframes b-image-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@keyframes b-image-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
